import { ReactNode, useState } from 'react';
import { useUserSettings } from '../../utils/use-user-settings';
import FeatureItem from './feature-item';
import { LandingAction, LandingActionButton } from './landing-action-button';
import VideoCard from './video-card';

import { alpha, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AutoStoriesOutlined from '@mui/icons-material/AutoStoriesOutlined';
import EventAvailableOutlined from '@mui/icons-material/EventAvailableOutlined';
import Lock from '@mui/icons-material/Lock';

interface EnterpriseFeatureLandingProps {
  title: string;
  description: string;
  topButtons: LandingAction[];
  featuresDescription: string[];
  learnMoreHref: string;
  videoCardBackground?: ReactNode;
}

export default function EnterpriseFeatureLanding({
  title,
  description,
  topButtons,
  featuresDescription,
  learnMoreHref,
  videoCardBackground,
}: EnterpriseFeatureLandingProps) {
  const theme = useTheme();
  const { marketingOverview } = useUserSettings();
  const [selectedMedia, setSelectedMedia] = useState(
    topButtons[0]?.mediaLink || ''
  );
  const [selectedLandingButton, setSelectedLandingButton] = useState(
    topButtons[0]?.label || topButtons[1]?.label || ''
  );

  const heightOfMarketingOverview = marketingOverview ? 48 : 0;

  const handleLandingButtonClick = (action: LandingAction) => {
    setSelectedLandingButton(action.label);
    setSelectedMedia(action.mediaLink);
  };

  const getLandingButtonAction = (action: LandingAction): LandingAction => ({
    ...action,
    variant: selectedLandingButton === action.label ? 'contained' : 'outlined',
  });

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: 'auto',
        boxSizing: 'border-box',
        overflowX: 'hidden',
        overflowY: 'hidden',
        bgcolor: alpha(theme.palette.background.default, 0.83),
        backdropFilter: 'blur(3px)',
        pb: 2,
        pt: 1,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundPosition: 'center top',
          backgroundSize: 'cover',
          filter: 'blur(10px)',
          opacity: 0.18,
          transform: 'scale(1.04)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          bgcolor: alpha(theme.palette.background.default, 0.83),
          backgroundSize: 'cover',
          filter: 'blur(8px)',
          opacity: 0.18,
        }}
      />

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          px: 4,
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: '33% 64%',
            },
            alignItems: 'start',
            gap: 5,
            minHeight: {
              md: `calc(100vh - 75px - ${heightOfMarketingOverview}px - 96px)`,
            },
          }}
        >
          {/* Left content */}
          <Stack
            spacing={1}
            sx={{
              alignItems: {
                xs: 'center',
                md: 'flex-start',
              },
              textAlign: {
                xs: 'center',
                md: 'left',
              },
              pt: 8,
            }}
          >
            <Stack spacing={5} sx={{ alignItems: 'flex-start', width: '100%' }}>
              <Stack>
                <Chip
                  icon={<Lock sx={{ fontSize: 16 }} />}
                  label="Enterprise Feature"
                  sx={{
                    mb: 2,
                    color: theme.palette.common.white,
                    fontWeight: 400,
                    fontSize: 16,
                    px: 1,
                    py: 2.5,
                    background: (theme) =>
                      `linear-gradient(90.07deg, ${theme.palette.cyan[500]} 0.06%, ${theme.palette.violet[500]} 99.94%)`,
                    '& .MuiChip-icon': {
                      color: theme.palette.common.white,
                    },
                  }}
                />
              </Stack>
              <Stack flexDirection="column" spacing={4}>
                <Stack
                  flexDirection="column"
                  spacing={2}
                  sx={{ width: '100%' }}
                >
                  <Stack
                    flexDirection="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography
                      variant="h3"
                      sx={{
                        color: theme.palette.semantic.text.title3,
                        fontWeight: 700,
                      }}
                    >
                      {title}
                    </Typography>

                    <Button
                      href={learnMoreHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: theme.palette.semantic.hyperlink.active,
                        fontWeight: 600,
                        fontSize: 14,
                        cursor: 'pointer',
                        textTransform: 'none',
                        border: 'none',
                        p: 0,
                        minWidth: 0,
                        '&:hover': {
                          backgroundColor: 'transparent',
                          color: theme.palette.semantic.hyperlink.hover,
                        },
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          borderBottom: `1px solid ${theme.palette.semantic.hyperlink.active}`,
                          lineHeight: 1.2,
                          '&:hover': {
                            borderBottom: 'none',
                          },
                        }}
                      >
                        <AutoStoriesOutlined sx={{ fontSize: 18 }} />
                        Learn More
                      </Box>
                    </Button>
                  </Stack>

                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 500,
                      color: theme.palette.semantic.text.body,
                    }}
                  >
                    {description}
                  </Typography>
                </Stack>
                <Stack
                  direction={{ xs: 'column', sm: 'row', md: 'row' }}
                  spacing={2}
                  sx={{
                    width: '100%',
                  }}
                >
                  {topButtons.map((action) => {
                    return (
                      <LandingActionButton
                        key={action.label}
                        action={getLandingButtonAction(action)}
                        onClick={() => handleLandingButtonClick(action)}
                      />
                    );
                  })}
                </Stack>
              </Stack>
              <Stack spacing={3} sx={{ pl: 1, my: 2 }}>
                {featuresDescription.map((f) => {
                  return <FeatureItem key={f} text={f} />;
                })}
              </Stack>

              <Stack
                direction="column"
                spacing={2}
                sx={{
                  width: '100%',
                  maxWidth: 330,
                }}
              >
                <Button
                  variant="contained"
                  href="https://mail.google.com/mail/?view=cm&fs=1&to=clientsuccess@promevo.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<EventAvailableOutlined />}
                  sx={{
                    width: '380px',
                    height: '48px',
                    textTransform: 'none',
                    fontSize: 15,
                    fontWeight: 500,
                    border: '0px',
                    cursor: 'pointer',
                    background: (theme) =>
                      `linear-gradient(90.07deg, ${theme.palette.cyan[500]} 0.06%, ${theme.palette.violet[500]} 99.94%)`,
                    '&:hover': {
                      background: (theme) =>
                        `linear-gradient(90.07deg, ${theme.palette.cyan[600]} 0.06%, ${theme.palette.violet[600]} 99.94%)`,
                    },
                  }}
                >
                  Book a Demo
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<AutoStoriesOutlined />}
                  href={learnMoreHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    width: '380px',
                    height: '48px',
                    textTransform: 'none',
                    fontSize: 15,
                    fontWeight: 500,
                    cursor: 'pointer',
                    borderColor: theme.palette.semantic.button.secondary.border,
                    color: theme.palette.semantic.button.secondary.text,
                    '&:hover': {
                      background: theme.palette.semantic.button.secondary.hover,
                    },
                  }}
                >
                  Learn More
                </Button>
              </Stack>
            </Stack>
          </Stack>

          {/* Right media */}
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'start',
              justifyContent: 'center',
              pt: 22,
              position: 'relative',
            }}
          >
            {videoCardBackground ? videoCardBackground : null}

            {/* Wrapped VideoCard */}
            <Box
              sx={{
                position: 'relative',
                zIndex: 1,
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <VideoCard src={selectedMedia} />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
